import React, {useEffect, useMemo} from 'react';
import {useAppDispatch, useAppSelector} from '../../store/hooks';
import {useTranslation} from 'react-i18next';
import {Helmet} from 'react-helmet';
import {Box, Divider, Tab, Tabs, styled} from '@mui/material';
import PageTitle from '../shared/PageTitle';
import {useParams, useSearchParams} from 'react-router-dom';
import {BORDER_COLOR, CARD_BORDER_RADIUS, GRAY_BG_COLOR} from '../../styles';
import PlayerProfileRow from './components/PlayerProfileRow';
import {getProfileDetailAsync} from '../../store/profileSlice';
import FullScreenLoadingSpinner from '../shared/FullScreenLoading';
import PlayerLoadRow from './components/PlayerLoadRow';
import ShiftMetricsRow from './components/ShiftMetricsRow';
import {getPlayerSessionsAsync} from '../../store/playerSessionSlice';
import {getTime} from '../../services/dateService';
import PlayerPlotChart from './components/PlayerPlotChart';
import {getBaselinesAsync} from '../../store/baselineSlice';
import PlayerRankings from './components/PlayerRankings';
import {Player} from '../../interfaces';
import PageContainer from '../shared/PageContainer';
import PlayerSessionsPanel from '../shared/PlayerSessionsPanel';
import PlayerSessionFilter from '../shared/PlayerSessionFilter';
import usePlayerSessionFilter from '../../hooks/usePlayerSessionFilter';
import {getLatestBaselineByType} from '../../services/baselineService';
import {SessionTypes} from '../../enums';

const DashboardWrapper = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: GRAY_BG_COLOR,
  borderRadius: CARD_BORDER_RADIUS,
}));

const StyledDivider = styled(Divider)(() => ({
  borderBottomWidth: '1px',
  borderBottomStyle: 'solid',
  borderBottomColor: BORDER_COLOR,
  marginTop: 10,
  marginBottom: 10,
}));

const PlayerDashboard: React.FC = () => {
  const {t} = useTranslation();
  const dispatch = useAppDispatch();
  const {profileDetail, baselines, acwrScores} = useAppSelector(state => ({
    profileDetail: state.profile.profileDetail,
    baselines: state.baseline?.baselines,
    acwrScores: state.profile.profileDetail?.acwrScores,
  }));
  const {
    filteredPlayerSessions,
    teams: playerSessionTeams,
    filter: playerSessionFilter,
    loading: playerSessionsLoading,
    onFilterUpdate,
  } = usePlayerSessionFilter();
  const {profileId, teamId} = useParams();

  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') === 'sessions' ? 1 : 0;

  const player: Player | null = useMemo(() => {
    if (!teamId) {
      return null;
    }
    const player = profileDetail?.players?.find(
      (p: Player) => p.team?.id === parseInt(teamId),
    );
    return player || null;
  }, [profileDetail?.players, teamId]);

  const {
    // avgShiftCount,
    // avgTOI,
    avgShiftTime,
    energyRatio,
  } = useMemo(() => {
    const mostRecentBaseline =
      getLatestBaselineByType(baselines)[SessionTypes.game];

    // const gameBaselines = (baselines ?? []).filter(
    //   bs => bs.type === SessionTypes.game,
    // );
    // const avgShiftCount = getAvgShiftCount(gameBaselines);
    // const avgTOI = getAvgTimeOnIce(gameBaselines);
    const avgShiftTime = mostRecentBaseline?.shiftTimeAvg ?? 0;
    const energyRatio = mostRecentBaseline?.energyRatio ?? 0;
    const rankingValues = () => {
      const relevantSkills = profileDetail?.skills?.find(
        s => s.gender && s.position,
      );
      return {
        strideSpeedScore: relevantSkills?.strideSpeedScore,
        strideSpeedProgress: relevantSkills?.strideSpeedProgress,
        explosivenessScore: relevantSkills?.explosivenessScore,
        explosivenessProgress: relevantSkills?.explosivenessProgress,
        agilityScore: relevantSkills?.agilityScore,
        agilityProgress: relevantSkills?.agilityProgress,
        balanceScore: relevantSkills?.balanceScore,
        balanceProgress: relevantSkills?.balanceProgress,
      };
    };
    return {
      // avgShiftCount,
      // avgTOI: getTime(avgTOI) || '0:00',
      avgShiftTime: getTime(avgShiftTime) || '0:00',
      energyRatio,
      rankingValues: rankingValues(),
    };
  }, [baselines, profileDetail?.skills]);

  useEffect(() => {
    if (profileId) {
      dispatch(getProfileDetailAsync({profileId: parseInt(profileId)}));
      dispatch(getPlayerSessionsAsync({profileId: parseInt(profileId)}));
      dispatch(
        getBaselinesAsync({
          profileId: parseInt(profileId),
          types: Object.values(SessionTypes),
        }),
      );
    }
  }, [dispatch, profileId]);

  if (!profileDetail) {
    return (
      <>
        <Helmet>
          <title>
            {t('navigation.pageTitle', {pageName: t('pages.playerDashboard')})}
          </title>
        </Helmet>
        <FullScreenLoadingSpinner />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>
          {t('navigation.pageTitle', {pageName: t('pages.playerDashboard')})}
        </title>
      </Helmet>
      <PageContainer>
        <PageTitle>{t('pages.playerDashboard')}</PageTitle>
        <Tabs
          value={tab}
          onChange={(_, v) => setSearchParams(v === 1 ? {tab: 'sessions'} : {})}
          sx={{mb: 2, borderBottom: 1, borderColor: 'divider'}}>
          <Tab label={t('pages.overview')} />
          <Tab label={t('pages.sessions')} />
        </Tabs>

        {tab === 0 && (
          <DashboardWrapper sx={{p: {xs: 1, md: 2}}}>
            <PlayerProfileRow profileDetail={profileDetail} player={player} />
            <StyledDivider />
            <PlayerLoadRow acwrScores={acwrScores ?? []} />
            <StyledDivider />
            <ShiftMetricsRow
              avgShiftTime={avgShiftTime}
              // avgTOI={avgTOI}
              // avgShiftCount={avgShiftCount}
              avgEnergyRatio={energyRatio}
            />
            <StyledDivider />
            <PlayerRankings
              skills={profileDetail?.skills ?? []}
              year={(profileDetail?.dateOfBirth || '').split('-')[0]}
              profileGender={profileDetail?.gender}
              profilePosition={profileDetail?.position}
            />
            <StyledDivider />
            <PlayerPlotChart playerName={profileDetail.firstName as string} />
          </DashboardWrapper>
        )}

        {tab === 1 && (
          <Box>
            <Box sx={{display: 'flex', justifyContent: 'flex-end', mb: 1}}>
              <PlayerSessionFilter
                onFilterUpdate={onFilterUpdate}
                currentFilters={playerSessionFilter}
                teams={playerSessionTeams ?? []}
              />
            </Box>
            <PlayerSessionsPanel
              navigateUrlPrefix={`/teams/${teamId}/players/${profileId}/playersessions`}
              playerSessions={filteredPlayerSessions}
              baselines={baselines}
              loading={playerSessionsLoading}
            />
          </Box>
        )}
      </PageContainer>
    </>
  );
};

export default PlayerDashboard;
